import { api } from './client';
import type { QuotesListResponse, QuoteResponse } from '../../types/quotes';
import type { DashboardStatisticsResponse, AdminDashboardStatisticsParams } from '../../types/dashboard';
import type { CarrierImportReport } from '../../types/companies';

export const companiesApi = {
  getAdminQuotes(filters: Record<string, string> = {}): Promise<QuotesListResponse> {
    const params = new URLSearchParams(filters);
    return api.request<QuotesListResponse>(`/admin/cargo_quote_requests?${params}`);
  },

  getAdminQuote(id: number | string): Promise<QuoteResponse> {
    return api.request<QuoteResponse>(`/admin/cargo_quote_requests/${id}`);
  },

  updateAdminQuote(id: number | string, data: any): Promise<QuoteResponse> {
    return api.request<QuoteResponse>(`/admin/cargo_quote_requests/${id}`, {
      method: 'PATCH',
      body: { cargo_quote_request: data } as any
    });
  },

  getAdminQuoteContactedCompanies(id: number | string): Promise<{ company_contacts: any[] }> {
    return api.request<{ company_contacts: any[] }>(`/admin/cargo_quote_requests/${id}/contacted_companies`);
  },

  getAdminTransportationCompanies(): Promise<{ transportation_companies: any[] }> {
    return api.request<{ transportation_companies: any[] }>('/admin/transportation_companies');
  },

  // Singular carrier detail. The index endpoint deliberately omits the
  // capacity fields (`max_weight`/`max_length`/`max_width`/`max_height`)
  // and `address`; only the `show` endpoint returns them. The Directory
  // View modal and Edit form hydrate from here so a list row that never
  // carried capacity values can still display/prefill the stored
  // capacity (#278).
  getAdminTransportationCompany(id: number | string): Promise<{ transportation_company: any }> {
    return api.request<{ transportation_company: any }>(`/admin/transportation_companies/${id}`);
  },

  createTransportationCompany(data: any): Promise<{ transportation_company: any }> {
    return api.request<{ transportation_company: any }>('/admin/transportation_companies', {
      method: 'POST',
      body: { transportation_company: data } as any
    });
  },

  updateTransportationCompany(id: number | string, data: any): Promise<{ transportation_company: any }> {
    return api.request<{ transportation_company: any }>(`/admin/transportation_companies/${id}`, {
      method: 'PATCH',
      body: { transportation_company: data } as any
    });
  },

  // Carrier Directory import (issue #178). The modal hands the backend
  // the file picked by the admin; the response is the row-level report
  // rendered immediately in the modal. The endpoint accepts only CSV
  // uploads — non-CSV bodies are rejected by CarrierCsvImport with a
  // localized `invalid_format` error. The ApiClient already serializes
  // JSON and omits the Content-Type for FormData, so we can pass the
  // FormData through untouched.
  importCarriers(file: File): Promise<CarrierImportReport> {
    const formData = new FormData();
    formData.append('file', file);
    return api.request<CarrierImportReport>('/admin/transportation_companies/import', {
      method: 'POST',
      body: formData,
    });
  },

  // Carrier Directory template (issues #176, #178). The backend
  // generates the CSV body in-memory with the current request locale's
  // header labels, so a single GET here lets the modal trigger the
  // download and the spreadsheet the admin opens is in their own
  // language. Uses the same download-with-headers pattern as the
  // existing quote attachment downloaders so the response's
  // Content-Disposition filename is honored (a future filename tweak
  // on the backend will not require a frontend change).
  downloadCarrierTemplate(): Promise<void> {
    return api.downloadAdminCarrierTemplate();
  },

  // Carrier Directory import guide (PDF). Mirrors the template
  // downloader: a single GET to the guide endpoint, the ApiClient
  // streams the PDF body to the browser and the modal button
  // triggers the download. The filename is read from
  // Content-Disposition when present.
  downloadCarrierGuide(): Promise<void> {
    return api.downloadAdminCarrierGuide();
  },

  // Carrier Status (#173) replaces the legacy deactivate/reactivate
  // endpoints. Status is edited through the standard PATCH endpoint
  // with an explicit `carrier_status` parameter. The previous
  // deactivate/reactivate methods are removed because the binary
  // `active` boolean is no longer the source of truth.
  //
  // (The previous deactivateTransportationCompany / reactivateTransportationCompany
  // methods have been removed in #173.)

  // Admin dashboard statistics. The recent-activity feed is server-paginated:
  // the backend extends this same endpoint with `recent_activity_page` /
  // `recent_activity_per_page` (default page 1, 10 per page) and returns the
  // page of `recent_activities` alongside a `recent_activities_pagination`
  // block. Omitting the params keeps the backend defaults, so callers that
  // don't care about the feed still get a valid response.
  getAdminDashboardStatistics(
    params: AdminDashboardStatisticsParams = {}
  ): Promise<DashboardStatisticsResponse> {
    const search = new URLSearchParams();
    if (params.recent_activity_page !== undefined) {
      search.set('recent_activity_page', String(params.recent_activity_page));
    }
    if (params.recent_activity_per_page !== undefined) {
      search.set('recent_activity_per_page', String(params.recent_activity_per_page));
    }
    const query = search.toString();
    return api.request<DashboardStatisticsResponse>(
      `/admin/dashboard/statistics${query ? `?${query}` : ''}`
    );
  },

  downloadAdminQuoteAttachment(quoteId: number | string, attachmentId: number | string): Promise<void> {
    return api.downloadAdminQuoteAttachment(quoteId, attachmentId);
  },

  downloadAllAdminQuoteAttachments(quoteId: number | string): Promise<void> {
    return api.downloadAllAdminQuoteAttachments(quoteId);
  },

  createQuoteResponse(quoteId: number | string, data: any): Promise<{ quote_response: any }> {
    return api.request<{ quote_response: any }>(`/admin/cargo_quote_requests/${quoteId}/quote_responses`, {
      method: 'POST',
      body: { quote_response: data } as any
    });
  },

  updateQuoteResponse(quoteId: number | string, responseId: number | string, data: any): Promise<{ quote_response: any }> {
    return api.request<{ quote_response: any }>(`/admin/cargo_quote_requests/${quoteId}/quote_responses/${responseId}`, {
      method: 'PATCH',
      body: { quote_response: data } as any
    });
  },

  deleteQuoteResponse(quoteId: number | string, responseId: number | string): Promise<{ message: string }> {
    return api.request<{ message: string }>(`/admin/cargo_quote_requests/${quoteId}/quote_responses/${responseId}`, {
      method: 'DELETE'
    });
  },

  markQuoteResponseContacted(quoteId: number | string, responseId: number | string): Promise<{ message: string; quote_response: any }> {
    return api.request<{ message: string; quote_response: any }>(`/admin/cargo_quote_requests/${quoteId}/quote_responses/${responseId}/mark_contacted`, {
      method: 'POST'
    });
  },
};